import OlMap, { MAP_TYPES } from '@m1/ol-map';
import useUpdateEffect from '@restart/hooks/useUpdateEffect';
import classNames from 'classnames/bind';
import React, { useState, useMemo, useRef } from 'react';
import { RiCheckboxBlankCircleLine, RiCheckboxCircleFill, RiMap2Line } from 'react-icons/ri';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

import AccordionCaret from './AccordionCaret';
import styles from './MapSelector.module.scss';

import actions from '@/actions';
import { getCSSVariable } from '@/utils/theme';

const cx = classNames.bind(styles);

// VWorld 3D 메뉴 항목 — OlMap을 사용하지 않고 별도 3D 모듈로 라우팅
const VWORLD_3D_ITEM = { mapId: 'VWORLD', label: 'VWorld - 3D' };

const MapSelector = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const [showPanel, setShowPanel] = useState(false);
  const [currentItem, setCurrentItem] = useState({
    mapId: 'GOOGLE',
    typeIndex: 0,
  });
  const menuRef = useRef();
  const panelRef = useRef();

  const title = useMemo(() => {
    if (currentItem.mapId === 'VWORLD') return VWORLD_3D_ITEM.label;
    return `Google - ${MAP_TYPES[currentItem.typeIndex].name}`;
  }, [currentItem]);

  // Allow 3D map only in Mission Hub
  const isAvailable3dMap = useMemo(() => {
    return location.pathname.startsWith('/mission-hub');
  }, [location]);

  useUpdateEffect(() => {
    if (showPanel) {
      document.addEventListener('mousedown', unfocusPanel);
    }

    return () => {
      document.removeEventListener('mousedown', unfocusPanel);
    };
  }, [showPanel]);

  const unfocusPanel = (e) => {
    if (menuRef.current.contains(e.target)) return;
    if (panelRef.current.contains(e.target)) return;

    setShowPanel(false);
  };

  const togglePanel = () => {
    setShowPanel(!showPanel);
  };

  const selectGoogle = (typeIndex) => {
    togglePanel();
    setCurrentItem({ mapId: 'GOOGLE', typeIndex });
    dispatch(actions.map3d.hide());
    OlMap.changeMap(typeIndex);
  };

  const selectVWorld3D = () => {
    togglePanel();
    setCurrentItem({ mapId: 'VWORLD', typeIndex: 0 });
    dispatch(actions.map3d.show());
  };

  return (
    <div className={cx('container')}>
      <div ref={menuRef} className={cx('menu')} onClick={togglePanel}>
        <RiMap2Line size={20} color="white" />
        <div className={cx('name')}>{title}</div>
        <AccordionCaret up={showPanel} size={32} />
      </div>
      <div ref={panelRef} className={cx(['panel', { show: showPanel }])}>
        {MAP_TYPES.map((type, typeIndex) => {
          const isActive = currentItem.mapId === 'GOOGLE' && currentItem.typeIndex === typeIndex;

          return (
            <div key={`GOOGLE-${typeIndex}`} onClick={() => selectGoogle(typeIndex)} className={cx('item')}>
              {isActive && <RiCheckboxCircleFill size={16} color={getCSSVariable('--tint-color')} />}
              {!isActive && <RiCheckboxBlankCircleLine size={16} color="white" />}
              <div className={cx(['menuText', { active: isActive }])}>Google - {type.name}</div>
            </div>
          );
        })}
        {isAvailable3dMap &&
          (() => {
            const isActive = currentItem.mapId === 'VWORLD';
            return (
              <div onClick={selectVWorld3D} className={cx('item')}>
                {isActive && <RiCheckboxCircleFill size={16} color={getCSSVariable('--tint-color')} />}
                {!isActive && <RiCheckboxBlankCircleLine size={16} color="white" />}
                <div className={cx(['menuText', { active: isActive }])}>{VWORLD_3D_ITEM.label}</div>
              </div>
            );
          })()}
      </div>
    </div>
  );
};

export default MapSelector;
